/**
 * The one Lipscore script loader. Every widget — top bar badge, reviews block,
 * product rating, review list, card stars — goes through it, so a page only
 * ever runs one Lipscore runtime, in one language.
 */

export const DEFAULT_LIPSCORE_COUNTRY = "sv";

const COUNTRY_CODE_PATTERN = /^[a-z]{2}$/;
// Any language counts: a second runtime breaks the widgets of the first.
const EXISTING_SCRIPT = "script[src*='lipscore-v1.js']";

/** A two-letter widget language, or the default when it is unset or invalid. */
export const resolveLipscoreCountry = (code?: string | null): string =>
	code && COUNTRY_CODE_PATTERN.test(code) ? code : DEFAULT_LIPSCORE_COUNTRY;

export const lipscoreScriptSrc = (code?: string | null): string =>
	`//static.lipscore.com/assets/${resolveLipscoreCountry(code)}/lipscore-v1.js`;

interface LipscoreRuntime {
	init?: (options: { apiKey: string }) => void;
	/** Initialises the widgets not drawn yet; leaves drawn ones alone. */
	initWidgets?: () => void;
	/** Redraws every widget on the page — takes no arguments. */
	reInitWidgets?: () => void;
}

export interface LipscoreWindow {
	lipscore?: LipscoreRuntime;
	lipscoreInit?: () => void;
}

export interface ScriptElement {
	async?: boolean;
	src?: string;
	onload?: (() => void) | null;
}

export interface LipscoreDocument {
	querySelector: (selector: string) => unknown;
	createElement: (tag: "script") => ScriptElement;
	head: { appendChild: (el: ScriptElement) => unknown };
}

// Widgets that mount together (rating, review list, card stars) land within this.
const REINIT_DELAY_MS = 50;
let reInitTimer: ReturnType<typeof setTimeout> | null = null;
let reInitForce = false;

/**
 * The one page-wide Lipscore re-scan. Lipscore's `reInitWidgets` redraws every
 * widget on the page whatever it is passed, so each newly mounted widget
 * calling it made the PDP rating blink: drawn, emptied when the related
 * products' card stars mounted, drawn again. A new widget only needs
 * `initWidgets`, which leaves drawn ones alone; `force` (`reInitWidgets`) is
 * for a widget whose data changed in place, a variant switch. Calls within the
 * window collapse into one, forced if any of them asked for it.
 */
export const scheduleLipscoreReInit = ({
	force = false,
	win = window as unknown as LipscoreWindow
}: { force?: boolean; win?: LipscoreWindow } = {}): void => {
	reInitForce ||= force;
	if (reInitTimer) clearTimeout(reInitTimer);
	reInitTimer = setTimeout(() => {
		const shouldForce = reInitForce;
		reInitTimer = null;
		reInitForce = false;
		if (shouldForce) win.lipscore?.reInitWidgets?.();
		else win.lipscore?.initWidgets?.();
	}, REINIT_DELAY_MS);
};

interface LoadOptions {
	apiKey: string;
	code?: string | null;
	/** Re-scan for widgets; defaults to Lipscore's own `reInitWidgets`. */
	onReady?: () => void;
	/** Injected in tests; the browser globals otherwise. */
	doc?: LipscoreDocument;
	win?: LipscoreWindow;
}

/** Client-only: call from `onMounted` or after a `nextTick`. */
export const loadLipscoreScript = ({
	apiKey,
	code,
	onReady,
	doc = document as unknown as LipscoreDocument,
	win = window as unknown as LipscoreWindow
}: LoadOptions): void => {
	const reInit = onReady ?? (() => scheduleLipscoreReInit({ win }));

	if (doc.querySelector(EXISTING_SCRIPT)) {
		reInit();
		return;
	}

	// Lipscore calls `window.lipscoreInit` itself on some loads (seen on a PDP)
	// but not on others (a search page: never initialised), so `onload` calls
	// it too, and the guard keeps the second call from initialising twice.
	let initialised = false;
	win.lipscoreInit = () => {
		if (initialised) return;
		initialised = true;
		win.lipscore?.init?.({ apiKey });
		reInit();
	};
	const script = doc.createElement("script");
	script.async = true;
	script.src = lipscoreScriptSrc(code);
	script.onload = () => win.lipscoreInit?.();
	doc.head.appendChild(script);
};
